import { useMutation } from '@tanstack/react-query';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { DeleteCohortData } from '@verantos-dev/types-cohorts';

import { cohortApiSlice } from '../cohortAPISlice';
import { COHORTS_BASE_PATH } from '../constants';
import { authFetch } from './fetchers';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';

/**
 * Canonical TanStack mutation for deleting a cohort profile
 * (DELETE /cohort-profile/:cohortId). The legacy RTK `useDeleteCohortMutation`
 * remains available for non-migrated callers; gen-3 delete flows use this hook.
 *
 * Tagged with `COHORT_WRITE_MUTATION_KEY` so `useProjectPageCohortSync`
 * invalidates the project artifact manifest + activity feed on success (OR-6111).
 */
export const useDeleteCohortProfileMutation = () => {
  const dispatch = useAppDispatch();

  return useMutation<void, Error, DeleteCohortData>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: ({ projectId, cohortId }) =>
      authFetch<void>(`/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, { cohortId }) => {
      // Keep legacy RTK caches in sync. Mirrors the RTK `deleteCohort` invalidatesTags.
      // TODO: cleanup with research_assistant_gen_3 ff
      dispatch(
        cohortApiSlice.util.invalidateTags([
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ])
      );
    },
  });
};
